import { IS_SUGGESTIVE_SELLING_V2_ENABLED } from "../data/constants";
import {
  getRecommendations,
  getRecommendationsV2,
  saveRecommendations,
} from "../redux-store/slices/RecommenderSlice";
import { buildRecommendationsV2Payload } from "./request";
import type { RecommendationsV1Payload } from "./types";

/**
 * `recommendations/engine` — the only place that knows HOW to talk to the recommendation service.
 *
 * ## This used to be a platform split. It is not any more.
 *
 * V2 was web-only for one reason at a time — the endpoint lived in a desktop-only variant, the
 * `recommendationEngine` base-URL switch existed in one axios interceptor, and the other
 * platform's `RecommenderSlice` had no `getRecommendationsV2` action or saga. All three are gone:
 *
 * | | where it is now |
 * |---|---|
 * | `getRecommendationsV2` endpoint | `config/endpoints/shared.ts` |
 * | `recommendationEngine` base-URL switch | both `utils/apiService` variants |
 * | V2 action + reducer | `RecommenderSlice/shared.ts` |
 * | V2 saga + response normalisation | `saga/recommender/shared.ts` |
 * | the engine-version flag | `data/constants/shared.ts` |
 * | the recommendation engine's host | web env var; mobile `config/index.js` → effects bridge |
 *
 * So this is one shared module: **web is the reference, and mobile runs the same flow.** Nothing
 * in the feature is per-platform any more — the sale-update effect's route guard closed too, via
 * `utils/navigation`'s `isNewOrderScreenActive()`.
 */

export type RecommendationEngineVersion = "v1" | "v2";

export const ENGINE_V1: RecommendationEngineVersion = "v1";
export const ENGINE_V2: RecommendationEngineVersion = "v2";

/** What a caller must supply for the engine to build whichever payload the live version needs. */
export interface RecommendationFetchInput {
  /** Built by the caller for V1; ignored on the V2 path. */
  v1Payload?: RecommendationsV1Payload;
  /** The sale the V2 payload is derived from; ignored on the V1 path. */
  saleData?: any;
  /** The customer store, for the V2 phone-number fallback; ignored on the V1 path. */
  customerStore?: any;
}

/**
 * V2 unless the `suggestiveSellingFlow` local-storage override says otherwise.
 *
 * A FUNCTION rather than a re-exported const, so that no caller pulls the value in at
 * module-eval time — see audit checks H/I. The underlying flag is itself an eval-time read, but
 * of a synchronous store with no bridge behind it, on both platforms.
 */
export function getRecommendationEngineVersion(): RecommendationEngineVersion {
  return IS_SUGGESTIVE_SELLING_V2_ENABLED ? ENGINE_V2 : ENGINE_V1;
}

/**
 * Can the engine answer without an item in the cart?
 *
 * V1 is asked "what goes with THIS item" and cannot; V2 is asked about the guest and the cart
 * together and can, which is what makes the on-arrival fetch possible.
 */
export function canFetchWithoutCart(): boolean {
  return getRecommendationEngineVersion() === ENGINE_V2;
}

/**
 * Ask the engine for suggestions, on whichever version is live.
 *
 * `skipGlobalLoader` is always set: suggestions are ambient, and a full-screen spinner over a
 * background refresh would block billing. Both platforms' request interceptors honour it.
 */
export function fetchRecommendations(
  dispatch: (action: any) => void,
  input: RecommendationFetchInput,
) {
  if (getRecommendationEngineVersion() === ENGINE_V2) {
    dispatch(
      getRecommendationsV2({
        dataToPost: buildRecommendationsV2Payload(input.saleData, input.customerStore),
        skipGlobalLoader: true,
      }),
    );
    return;
  }
  if (!input.v1Payload) return;
  dispatch(getRecommendations({ dataToPost: input.v1Payload, skipGlobalLoader: true }));
}

/** Drop whatever is on screen — the cart emptied, or the operator left the ordering screen. */
export function clearRecommendations(dispatch: (action: any) => void) {
  dispatch(saveRecommendations({}));
}
